import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Lock, Mail, User, Loader2, CheckCircle, XCircle } from "lucide-react";

export const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  // Password validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
  };
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error("Please ensure your password meets all requirements");
      return;
    }
    
    if (!passwordsMatch) {
      toast.error("Passwords do not match");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/register`, { name, email, password });
      setSuccess(true);
      toast.success("Registration successful! Please check your email to verify your account.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md mx-4 border border-slate-200 shadow-xl bg-white">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Mail className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-slate-900">Check your email</h2>
            <p className="text-slate-500 mb-6">
              We've sent a verification link to <strong className="text-slate-900">{email}</strong>. 
              Click the link to verify your account.
            </p>
            <Button 
              variant="outline" 
              onClick={() => navigate("/login")}
              className="w-full h-11"
            >
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#165581] p-12 flex-col justify-between relative overflow-hidden">
        {/* Soft background shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        </div>
        
        {/* Logo on blue background */}
        <div className="relative z-10">
          <div className="flex items-center gap-4">
            <img src="/logo-blue-bg.png" alt="eSay" className="w-20 h-20" />
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight">eSay</h1>
              <p className="text-white/80 text-sm font-medium">Email Marketing</p>
            </div>
          </div>
        </div>
        
        {/* Tagline */}
        <div className="relative z-10">
          <h2 className="text-5xl font-bold text-white leading-tight mb-6">
            Start Growing<br/>
            <span className="text-white/90">Your Business.</span>
          </h2>
          <p className="text-xl text-white/80 max-w-md leading-relaxed">
            Join thousands of businesses using eSay to reach more customers.
          </p>
        </div>
        
        <div className="relative z-10" />
      </div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50 overflow-y-auto">
        <Card className="w-full max-w-md border border-slate-200 shadow-xl bg-white" data-testid="register-card">
          <CardHeader className="space-y-1 text-center pb-2">
            {/* Logo on white - shown on mobile and above form */}
            <div className="flex items-center justify-center mb-4">
              <img src="/logo-white-bg.png" alt="eSay" className="w-16 h-16" />
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">
              Create Account
            </CardTitle>
            <CardDescription className="text-slate-500">
              Start your email marketing journey
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-11 h-12 border-slate-200 focus:border-[#165581] focus:ring-[#165581]"
                  data-testid="name-input"
                  required
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 h-12 border-slate-200 focus:border-[#165581] focus:ring-[#165581]"
                  data-testid="email-input"
                  required
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 h-12 border-slate-200 focus:border-[#165581] focus:ring-[#165581]"
                  data-testid="password-input"
                  required
                />
              </div>
              
              {/* Password requirements */}
              {password && (
                <div className="space-y-1.5 text-sm bg-slate-50 p-3 rounded-lg">
                  <PasswordCheck passed={passwordChecks.length} label="At least 8 characters" />
                  <PasswordCheck passed={passwordChecks.uppercase} label="One uppercase letter" />
                  <PasswordCheck passed={passwordChecks.lowercase} label="One lowercase letter" />
                  <PasswordCheck passed={passwordChecks.number} label="One number" />
                </div>
              )}
              
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`pl-11 h-12 border-slate-200 focus:ring-[#165581] ${confirmPassword && !passwordsMatch ? 'border-red-300 focus:border-red-500' : 'focus:border-[#165581]'}`}
                  data-testid="confirm-password-input"
                  required
                />
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-red-500 text-sm">Passwords do not match</p>
              )}
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-[#165581] hover:bg-[#1a6a9e] text-white font-semibold shadow-lg shadow-[#165581]/25 hover:shadow-[#165581]/40 transition-all"
                disabled={loading || !isPasswordValid || !passwordsMatch}
                data-testid="register-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating account...
                  </span>
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-3 text-slate-400 font-medium">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-slate-200 hover:bg-slate-50 font-medium"
              onClick={handleGoogleLogin}
              data-testid="google-register-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <p className="text-center text-sm text-slate-500 mt-6">
              Already have an account?{" "}
              <Link 
                to="/login" 
                className="text-#165581 hover:text-#1a6a9e font-semibold"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const PasswordCheck = ({ passed, label }) => (
  <div className={`flex items-center gap-2 ${passed ? 'text-emerald-600' : 'text-slate-400'}`}>
    {passed ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
    <span>{label}</span>
  </div>
);

export default RegisterPage;
